import * as React from 'react';
import { UploadFailed } from './upload-failed.component';
import { DtpResult } from './dtp-result.component';
import { IAppContext, withAppContext } from 'src/App/shared/app-context';
import { ILogoStateInfo, LogoColorVariant, LogoSizeVariant, ILogoVersionInfo } from 'src/App/shared/logo.types';
import { UploadLogo } from './upload-logo.component';
import { PreviewLogo } from 'src/App/shared/components/preview-logo.component';
import { DebuggerInstance } from 'src/App/shared/debugger';
import { onCloseEventName } from 'src/App/shared/constants';

export enum UploadState {
    Upload,
    UploadFailed,
    Review,
    DtpResult,
    Completed
}

export interface ILogoProcessingResult {
    logoId: string;
}

export interface ILogoUploadProps {
    appContext: IAppContext
    onUploadSuccess(logoId: string): void;
    onUploadFail(): void;
}

interface IState {
    uploadState: UploadState;
    uploadError: string | null;
    logoInfo: ILogoStateInfo | ILogoProcessingResult | null; 
    showDtp: boolean;
    dtpResult: boolean;
    fileName?: string;
}

class LogoUploadComponent extends React.PureComponent<ILogoUploadProps,IState> {
    private onCloseHandle = () => {
        if (this.state.uploadState === UploadState.Review) {
            this.props.appContext.raiseTronlogoEvent({
                type: 'UPLOADCANCEL',
                motiveId: this.state.logoInfo !== null ? this.state.logoInfo.logoId : undefined,
                uploadFile: this.state.fileName
            })
        } 
    }

    constructor(props: ILogoUploadProps) {
        super(props);

        this.state = {
            uploadState: UploadState.Upload,
            uploadError: null,
            logoInfo: null,
            showDtp: false,
            dtpResult: false
        };
    }

    public componentDidMount() {
        this.props.appContext.appEvents.addListener(onCloseEventName, this.onCloseHandle);
        this.props.appContext.raiseTronlogoEvent({
            type: 'OPENUPLOAD'
        });
    }

    public componentWillUnmount() {
        this.props.appContext.appEvents.removeListener(onCloseEventName, this.onCloseHandle);
    }

    private getBaseApiUrl(): string {
        const apiKey = this.props.appContext.getApiKey();
        const userId = this.props.appContext.getUserId();
        const api = `/api/${apiKey}/user/${userId}/logo-upload/`;

        return api;
    }

    private createLogoStateInfo(logoId: string): ILogoStateInfo {
        const api = this.getBaseApiUrl() + logoId;

        return {
            logoId,
            normal: {
                variant: LogoColorVariant.normal,
                src: api + '?color_variant=' + LogoColorVariant.normal + '&size_variant=' + LogoSizeVariant.Preview,
                isLoaded: false,
                accept: true,
            },
            white: {
                variant: LogoColorVariant.white,
                src: api + '?color_variant=' + LogoColorVariant.white + '&size_variant=' + LogoSizeVariant.Preview,
                isLoaded: false,
                accept: true,
            },
            black: {
                variant: LogoColorVariant.black,
                src: api + '?color_variant=' + LogoColorVariant.black + '&size_variant=' + LogoSizeVariant.Preview,
                isLoaded: false,
                accept: true,
            }
        };
    }

    private onUploadCompleted(success: boolean, data: ILogoProcessingResult | null, message: string | null, fileName?: string) {
        DebuggerInstance.log('controller - upload completed');
        DebuggerInstance.log(`success: ${success}`);
        DebuggerInstance.log(data);
        DebuggerInstance.log(`message: ${message}`);

        let upload = UploadState.Upload;
        let logoInfo: ILogoStateInfo | ILogoProcessingResult | null = data;

        // successfull upload -> go to review screen
        if (success) {
            if (data !== null) {
                upload = UploadState.Review;
                logoInfo = this.createLogoStateInfo(data.logoId);
            } else {
                const error = new Error('Upload retured success but not data!');
                throw error;
            }
        } else {
            if (data !== null) {
                // go to Dtp request screen
                upload = UploadState.UploadFailed;
            }

            // upload failed event 
            this.props.appContext.raiseTronlogoEvent({
                type: 'UPLOADFAIL',
                errormsg: message as string,
                uploadFile: fileName
            });
        }
        
        DebuggerInstance.log(`state: ${upload}`);
        DebuggerInstance.log(`logo info: ${logoInfo}`);
        
        // upload celeho loga
        this.setState({
            uploadState: upload,
            uploadError: message,
            logoInfo,
            fileName
        });
    }

    private handleConfirmationChanged(version: ILogoVersionInfo, confirn: boolean): void {
        DebuggerInstance.log('handleConfirmationChanged');
        DebuggerInstance.log(version);
        DebuggerInstance.log(confirn);

        if(this.state.logoInfo === null) {
            throw new Error("Invalid state of application!");
        }

        const logo: ILogoStateInfo = Object.assign({}, this.state.logoInfo as ILogoStateInfo);
        switch (version.variant) {
            case LogoColorVariant.normal:
                logo.normal.accept = confirn;
                break;
            case LogoColorVariant.white:
                logo.white.accept = confirn;
                break;
            case LogoColorVariant.black:
                logo.black.accept = confirn;               
                break;
        }
        this.setState({
            logoInfo: logo,
            showDtp: !logo.normal.accept
        });
        DebuggerInstance.log(this.state);
    }

    private handleLogoLoaded(version: ILogoVersionInfo, success: boolean): void {
        if(this.state.logoInfo === null) {
            throw new Error("Invalid state of application!");
        }

        const logo: ILogoStateInfo = this.state.logoInfo as ILogoStateInfo;
        switch (version.variant) {
            case LogoColorVariant.normal:
                logo.normal.isLoaded = success;
                break;
            case LogoColorVariant.white:
                logo.white.isLoaded = success;
                break;
            case LogoColorVariant.black:
                logo.black.isLoaded = success;                
                break;
        }
        this.setState({logoInfo: logo});
    }

    private handleRequestReupload(): void {
        this.setState({
            uploadState: UploadState.Upload,
            uploadError: null,
            logoInfo: null
        });
    }

    private handleDtpSendCompleted(result: boolean, isAfterFailedUpload: boolean, email?: string, errorMsg?: string) {
        DebuggerInstance.log(`handleDtpSendCompleted with ${result}`);

        this.props.appContext.raiseTronlogoEvent({
            type: isAfterFailedUpload ? 'UPLOADFAIL' : 'UPLOADWRONG',
            motiveId: this.state.logoInfo !== null ? this.state.logoInfo.logoId : undefined,
            email,
            errormsg: errorMsg
        })

        this.setState({
            uploadState: UploadState.DtpResult,
            uploadError: null,
            logoInfo: null,
            showDtp: false,
            dtpResult: result
        });
    }

    private onPreviewCompleted(success: boolean): void {
        this.setState({
            uploadState: UploadState.Completed
        });

        if (success && this.state.logoInfo && this.state.logoInfo.logoId) {
            this.props.appContext.raiseTronlogoEvent({
                type: 'UPLOADDONE',
                motiveId: this.state.logoInfo.logoId,
                uploadFile: this.state.fileName
            })
            this.props.onUploadSuccess(this.state.logoInfo.logoId);
        } else {
            this.props.onUploadFail();
        }
    }

    private getContentFromState(): JSX.Element {
        let content: JSX.Element;
        
        switch (this.state.uploadState) {
            case UploadState.Upload:
                // show logo upload
                content = (
                    <UploadLogo
                        apiUrl={this.getBaseApiUrl()}
                        onUploadCompleted={(res, data, err, fileName) => this.onUploadCompleted(res, data, err, fileName)}
                    />
                );
                break;
            case UploadState.Completed:
            case UploadState.Review:
                // show logo preview
                content = (
                    <PreviewLogo
                        onCompleted={e => this.onPreviewCompleted(e)}
                        handleConfirmationChanged={(version, confirn) => this.handleConfirmationChanged(version, confirn)}
                        handleLogoLoaded={(version, success) => this.handleLogoLoaded(version, success)}
                        onDtpSendCompleted={(res, email, errorMsg) => this.handleDtpSendCompleted(res, false, email, errorMsg)}
                        logo={this.state.logoInfo as ILogoStateInfo}
                        apiUrl={this.getBaseApiUrl()}
                        enableCancel={true}
                        enableConfirm={true}
                        showDtp={this.state.showDtp}
                    />
                );
                break;
            case UploadState.UploadFailed:
                // show reupload form
                content = (
                    <UploadFailed
                        logoId={(this.state.logoInfo as ILogoProcessingResult).logoId}
                        apiUrl={this.getBaseApiUrl()}
                        errorMessage={this.state.uploadError as string}
                        requestReupload={() => this.handleRequestReupload()}
                        onDtpSendCompleted={(res, email, errorMsg) => this.handleDtpSendCompleted(res, true, email, errorMsg)}
                    />
                );
                break;
            case UploadState.DtpResult:
                content = (
                    <DtpResult
                        success={this.state.dtpResult}
                        onClose={() => this.props.onUploadFail()}
                    />
                );
                break;
            default:
                content = <h3>App is not in valid state!</h3>
        }

        return content;
    }

    public render() {
        const content = this.getContentFromState();

        return (
            <div id="logo-upload-controller"
                className="controller logo-upload-controller"
                >
                <div className="controller-content">{content}</div>
            </div>
        );
    }        
}

export const LogoUpload = withAppContext(LogoUploadComponent);



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-upload/logo-upload.component.tsx